// POI enrichment cache: client
//
// Wraps the server's /poi/* endpoints. Anonymous shared cache: a POI enriched
// by user A is reused by user B if requested within the TTL window.
//
// Graceful degradation: any network/HTTP error (including 503 when the server
// has no DATABASE_URL) returns null/empty without throwing: the enrichment
// pipeline keeps working as if no cache existed.

import type { EnrichedData, POI } from "../types";
import { API_BASE } from "./enrichment/proxy-api";

const DEFAULT_TIMEOUT_MS = 5_000;
/** Must stay ≤ the server's MAX_BATCH_KEYS (200). */
const BATCH_CHUNK_SIZE = 200;

export interface CachedEnrichment {
  osm_type: "node" | "way" | "relation";
  osm_id: string;
  category: string;
  lat: number;
  lon: number;
  name: string | null;
  enrichment: EnrichedData;
  enriched_at: string;
  is_stale: boolean;
}

interface BatchResponse {
  requested: number;
  hits: number;
  misses: number;
  results: CachedEnrichment[];
}

/** A POI is cacheable iff it has both osmType and osmId from OSM. */
export function isCacheablePoi(
  poi: POI,
): poi is POI & { osmId: number; osmType: "node" | "way" | "relation" } {
  return (
    typeof poi.osmId === "number" &&
    Number.isFinite(poi.osmId) &&
    poi.osmId > 0 &&
    (poi.osmType === "node" || poi.osmType === "way" || poi.osmType === "relation")
  );
}

/** `osm_type/osm_id`, the shared cache key; null for a POI without OSM identity */
export function poiCacheKey(poi: POI): string | null {
  if (!isCacheablePoi(poi)) return null;
  return `${poi.osmType}/${poi.osmId}`;
}

/**
 * Batch lookup: returns a Map keyed by `osm_type/osm_id` for the POIs that have
 * a cached enrichment. POIs without osmId/osmType are silently skipped.
 *
 * Returns an empty Map on any failure (network, 503, malformed body).
 */
export async function lookupPoiBatch(pois: POI[]): Promise<Map<string, CachedEnrichment>> {
  const result = new Map<string, CachedEnrichment>();
  const cacheable = pois.filter(isCacheablePoi);
  if (cacheable.length === 0) return result;

  const keys = cacheable.map((p) => ({
    osm_type: p.osmType,
    osm_id: String(p.osmId),
  }));

  // The server rejects >MAX_BATCH_KEYS (200) with a 400: chunk and merge
  // so large routes still benefit from the cache. A failed chunk degrades to a miss.
  for (let i = 0; i < keys.length; i += BATCH_CHUNK_SIZE) {
    const chunk = keys.slice(i, i + BATCH_CHUNK_SIZE);
    try {
      const res = await fetch(`${API_BASE}/poi/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keys: chunk }),
        signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
      });
      // Still degrade gracefully (empty Map), but don't do it *silently*:
      // a cache outage looks identical to an all-miss otherwise.
      if (!res.ok) {
        console.warn(`Ravitools: POI cache unavailable (HTTP ${res.status}): enriching without it`);
        continue;
      }
      const body = (await res.json()) as BatchResponse;
      if (!Array.isArray(body?.results)) {
        console.warn("Ravitools: POI cache returned a malformed body: enriching without it");
        continue;
      }
      for (const r of body.results) {
        result.set(`${r.osm_type}/${r.osm_id}`, r);
      }
    } catch (err) {
      console.warn("Ravitools: POI cache request failed: enriching without it", err);
    }
  }
  return result;
}

/**
 * Push an enrichment result to the shared cache.
 * No-op (returns false) if the POI has no osm identifier or any error occurs.
 */
export async function uploadPoiEnrichment(poi: POI, enrichment: EnrichedData): Promise<boolean> {
  if (!isCacheablePoi(poi)) return false;

  try {
    const res = await fetch(`${API_BASE}/poi/${poi.osmType}/${poi.osmId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        category: poi.category,
        lat: poi.lat,
        lon: poi.lon,
        name: poi.name,
        enrichment,
      }),
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
    });
    return res.ok;
  } catch {
    return false;
  }
}
